import { useState, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { useVideo } from "../../../hooks/useVideo";
import ConfirmationModal from "../../confirmation/ConfirmationModal";
import { generatePageNumbers } from "../../../util/generatePageNumber";
import {
  useDeleteVideoMutation,
  useGetAdminVideosQuery,
  useSetFeaturedVideoMutation,
} from "../../../features/api/videoApiSlice";
import LoadingSpinner from "../../loader/Loader";
import CustomDateModal from "./DateModal";

const AdminVideoList = () => {
  const navigate = useNavigate();
  const { formatDate } = useVideo();
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [deleteVideo] = useDeleteVideoMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [featuredDate, setFeaturedDate] = useState(null);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);

  // ✅ Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8; // categories per page
  const [setFeaturedVideo, { isLoading: isSetting }] =
    useSetFeaturedVideoMutation();

  const {
    data: videos = [], // ✅ safe default so maps never blow up
    isFetching, // use this for spinner
    isError,
    isLoading,
    refetch,
  } = useGetAdminVideosQuery(undefined, {
    refetchOnMountOrArgChange: "always", // ✅ refetch every time page mounts
    refetchOnFocus: true, // refresh when tab regains focus
    refetchOnReconnect: true, // refresh when network comes back
  });

  useEffect(() => {
    if (!videos?.length && !isFetching && !isError) {
      refetch(); // one more nudge in case the first request was aborted in dev
    }
  }, [videos?.length, isFetching, isError, refetch]);

  const [videoFilter, setVideoFilter] = useState("");

  // Filtered transactions
  const filteredVideos = useMemo(() => {
    if (!videos) return [];
    return videos.filter((tx) => {
      const matchesVideo =
        !videoFilter ||
        `${tx?.videoTitle} ${tx?.videoDescription}`
          .toLowerCase()
          .includes(videoFilter.toLowerCase());
      return matchesVideo;
    });
  }, [videos, videoFilter]);

  const totalPages = Math.ceil(filteredVideos.length / pageSize);

  const currentVideos = useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    return filteredVideos.slice(startIdx, startIdx + pageSize);
  }, [filteredVideos, currentPage, pageSize]);

  const pageNumbers = useMemo(
    () => generatePageNumbers(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const handlePageClick = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteClick = (videoId) => {
    setVideoToDelete(videoId);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!videoToDelete) return;
    try {
      const response = await deleteVideo({ videoId: videoToDelete }).unwrap();
      toast.success(response?.message || "Video deleted successfully!");
      setVideoToDelete(null);
      setIsModalOpen(false);
    } catch (err) {
      console.error("Failed to delete video:", err);
      toast.error(err?.data?.message || "Failed to delete video.");
    }
  };

  const handleCancelDelete = () => {
    setVideoToDelete(null);
    setIsModalOpen(false);
  };

  const openDateModal = (video) => {
    setSelectedVideoId(video._id);
    setFeaturedDate(video.featuredUntil ? new Date(video.featuredUntil) : null);
    setIsDateModalOpen(true);
  };

  const closeDateModal = () => {
    setSelectedVideoId(null);
    setFeaturedDate(null);
    setIsDateModalOpen(false);
  };

  const handleSaveFeatured = async () => {
    if (!selectedVideoId || !featuredDate) return;
    try {
      const response = await setFeaturedVideo({
        videoId: selectedVideoId,
        featuredUntil: featuredDate,
      }).unwrap();
      toast.success(response?.message || "Video set as featured!");
      closeDateModal();
    } catch (err) {
      console.error("Failed to set featured video:", err);
      toast.error(err?.data?.message || "Failed to set featured video.");
    }
  };

  if (isLoading || isSetting) return <LoadingSpinner />;

  if (!videos || videos?.length === 0) {
    return (
      <div className="no-content">
        <div>
          <p>No videos found</p>
        </div>
      </div>
    );
  } else if (isError)
    return (
      <div className="no-content">
        <div>
          <p>Error loading videos</p>
        </div>
      </div>
    );

  return (
    <div className="transactions-container">
      <div className="dashboard-top-section">
        <div className="dash-top-flex">
          <h1 className="dashboard-title">Total Videos {videos?.length}</h1>
          <button onClick={() => navigate("/admin/videos/add-video")}>
            Add Video
          </button>
        </div>
      </div>
      <div className="filters">
        <input
          type="text"
          placeholder="Filter by first or last name"
          value={videoFilter}
          onChange={(e) => setVideoFilter(e.target.value)}
        />
      </div>

      <table className="transactions-table">
        <thead>
          <tr>
            <th>No.</th>
            <th>Poster</th>
            <th>title</th>
            <th>Video Type</th>
            <th>Views</th>
            <th>Shares</th>
            <th>Featured</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentVideos?.map((tx, index) => (
            <tr key={tx?._id}>
              <td>{index + 1}</td>
              <td>
                <img
                  className="table-image"
                  src={tx?.posterUrl || tx?.thumbUrl}
                  alt={tx?.videoTitle}
                />
              </td>
              <td>{tx?.videoTitle}</td>
              <td>{tx?.videoType}</td>
              <td>{tx?.numViews?.length}</td>
              <td>{tx?.shares}</td>
              <td>{tx?.featured ? "Yes" : "No"}</td>
              <td>{formatDate(tx?.createdAt)}</td>
              <td className="actions-container">
                <button
                  className="refund-btn"
                  onClick={() => handleDeleteClick(tx?.id || tx?._id)}
                >
                  Delete
                </button>
                <button
                  className="feat-button"
                  onClick={() => openDateModal(tx)}
                  disabled={isSetting}
                >
                  {tx.isFeatured ? "Featured ✅" : "Set Featured"}
                </button>
                <Link
                  className="ed-button"
                  aria-label={`edit ${tx?.videoTitle}`}
                  to={`/admin/videos/edit-video/${tx?._id}`}
                >
                  Edit
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
        <ConfirmationModal
          isOpen={isModalOpen}
          message="Are you sure you want to delete this Video? This action cannot be undone."
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
        <CustomDateModal
          isOpen={isDateModalOpen}
          onClose={closeDateModal}
          onSave={handleSaveFeatured}
          featuredDate={featuredDate}
          setFeaturedDate={setFeaturedDate}
          isSetting={isSetting}
        />
      </table>
      {/* ✅ Pagination */}
      {totalPages > 1 && (
        <div className="pagination responsive-pagination">
          <button
            onClick={() => handlePageClick(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
          >
            {"<"}
          </button>
          {pageNumbers.map((page, idx) =>
            page === "..." ? (
              <span className="ellipsis" key={idx}>
                ...
              </span>
            ) : (
              <button
                key={idx}
                className={currentPage === page ? "active" : ""}
                onClick={() => handlePageClick(page)}
              >
                {page}
              </button>
            )
          )}
          <button
            onClick={() =>
              handlePageClick(Math.min(currentPage + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            {">"}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminVideoList;
